import { applyEncabezadoFiltrosUiFromFallback } from './reporteFiltrosEncabezadoExport';

export const INGRESO_POR_DIA_TITULO_DEFAULT = 'INGRESOS';

/**
 * Columnas fijas de la respuesta actual.
 * sourceKey = clave en el objeto de la fila (json.rows[i])
 */
export const INGRESO_POR_DIA_COLUMNS = [
  { id: 'fecha',     sourceKey: 'fecha',     label: 'Fecha',     align: 'left',  kind: 'text',  total: false },
  { id: 'total',     sourceKey: 'total',     label: 'Total',     align: 'right', kind: 'money', total: true  },
  { id: 'cortesias', sourceKey: 'cortesias', label: 'Cortesías', align: 'right', kind: 'money', total: true  },
  { id: 'venta',     sourceKey: 'venta',     label: 'Venta',     align: 'right', kind: 'money', total: true  },
  { id: 'efectivo',  sourceKey: 'efectivo',  label: 'Efectivo',  align: 'right', kind: 'money', total: true  },
  { id: 'tarjetas',  sourceKey: 'tarjetas',  label: 'Tarjetas',  align: 'right', kind: 'money', total: true  },
  { id: 'otros',     sourceKey: 'otros',     label: 'Otros',     align: 'right', kind: 'money', total: true  },
  { id: 'sueldos',   sourceKey: 'sueldos',   label: 'Sueldos',   align: 'right', kind: 'money', total: true  },
  { id: 'gastos',    sourceKey: 'gastos',    label: 'Gastos',    align: 'right', kind: 'money', total: true  },
  { id: 'final',     sourceKey: 'final',     label: 'Final',     align: 'right', kind: 'money', total: true  },
];

/**
 * Aplica etiquetas de json.columnas a las columnas base.
 * @param {Record<string,string>|null} columnas
 * @param {typeof INGRESO_POR_DIA_COLUMNS} [baseCols]
 */
export function applyIngresoPorDiaColumnLabels(columnas, baseCols = INGRESO_POR_DIA_COLUMNS) {
  if (!columnas || typeof columnas !== 'object') return baseCols;
  return baseCols.map((c) => {
    const key = c.sourceKey || c.id;
    const lbl = columnas[key];
    return typeof lbl === 'string' && lbl.trim() ? { ...c, label: lbl.trim() } : c;
  });
}

/**
 * Extrae el encabezado plano que devuelve la API.
 * @param {object|null} apiEnc  json.encabezado
 */
export function extractIngresoPorDiaEncabezado(apiEnc) {
  if (!apiEnc || typeof apiEnc !== 'object') return {};
  return {
    nombreComercial: String(apiEnc.empresaNombre || '').trim() || null,
    sucursalAppend:  String(apiEnc.sucursalNombre || '').trim() || null,
    domicilioFiscal: String(apiEnc.domicilioFiscal || '').trim() || null,
    rfc:             String(apiEnc.rfc             || '').trim() || null,
    periodoTexto:    String(apiEnc.periodoTexto    || '').trim() || null,
    turnosTexto:     String(apiEnc.turnosTexto     || '').trim() || null,
    cajasTexto:      String(apiEnc.cajasTexto      || '').trim() || null,
    reporteTitulo:   String(apiEnc.titulo          || INGRESO_POR_DIA_TITULO_DEFAULT).trim().toUpperCase(),
  };
}

/**
 * Mezcla el encabezado de la API con el fallback de sesión.
 */
export function mergeIngresoPorDiaEncabezado(apiEnc, fallback) {
  const a  = apiEnc   && typeof apiEnc   === 'object' ? apiEnc   : {};
  const fb = fallback && typeof fallback === 'object' ? fallback : {};
  const merged = {
    nombreComercial: a.nombreComercial || fb.nombreComercial || fb.empresa?.nombre || null,
    sucursalAppend:  a.sucursalAppend  || null,
    domicilioFiscal: a.domicilioFiscal || fb.domicilioFiscal || null,
    rfc:             a.rfc             || fb.rfc             || null,
    periodoTexto:    a.periodoTexto    || fb.periodoTexto    || null,
    turnosTexto:     a.turnosTexto     || fb.turnosTexto     || null,
    cajasTexto:      a.cajasTexto      || fb.cajasTexto      || null,
    sucursalesTexto: a.sucursalesTexto || fb.sucursalesTexto || null,
    sucursalesItems: Array.isArray(a.sucursalesItems) && a.sucursalesItems.length
      ? a.sucursalesItems
      : fb.sucursalesItems || [],
    cajasItems: Array.isArray(a.cajasItems) && a.cajasItems.length
      ? a.cajasItems
      : fb.cajasItems || [],
    reporteTitulo:   a.reporteTitulo   || INGRESO_POR_DIA_TITULO_DEFAULT,
  };
  return applyEncabezadoFiltrosUiFromFallback(merged, fb);
}

/**
 * Normaliza una fila cruda de json.rows.
 * @param {object} raw
 */
export function normalizeIngresoPorDiaRow(raw) {
  return {
    fecha:     raw.dateLabel || raw.date || raw.fecha || '',
    total:     raw.total,
    cortesias: raw.cortesias,
    venta:     raw.venta,
    efectivo:  raw.efectivo,
    tarjetas:  raw.tarjetas,
    otros:     raw.otros,
    sueldos:   raw.sueldos,
    gastos:    raw.gastos,
    final:     raw.final,
    byBranch:  Array.isArray(raw.byBranch) ? raw.byBranch : [],
  };
}

/**
 * Formatea una celda según su tipo (money / text).
 */
export function formatIngresoPorDiaCell(col, value) {
  if (value == null || value === '') return '0,00';
  if (col.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
}

/**
 * Obtiene los totales: primero desde json.totales, luego suma las filas.
 */
export function getIngresoPorDiaTotals(normRows, apiTotales) {
  if (apiTotales && typeof apiTotales === 'object') {
    const has = (k) => apiTotales[k] != null && !Number.isNaN(Number(apiTotales[k]));
    if (has('total') || has('final')) {
      return {
        total:     Number(apiTotales.total     ?? 0),
        cortesias: Number(apiTotales.cortesias ?? 0),
        venta:     Number(apiTotales.venta     ?? 0),
        efectivo:  Number(apiTotales.efectivo  ?? 0),
        tarjetas:  Number(apiTotales.tarjetas  ?? 0),
        otros:     Number(apiTotales.otros     ?? 0),
        sueldos:   Number(apiTotales.sueldos   ?? 0),
        gastos:    Number(apiTotales.gastos    ?? 0),
        final:     Number(apiTotales.final     ?? 0),
      };
    }
  }
  const sum = (k) => normRows.reduce((s, r) => s + (Number.isNaN(Number(r[k])) ? 0 : Number(r[k])), 0);
  return {
    total:     sum('total'),
    cortesias: sum('cortesias'),
    venta:     sum('venta'),
    efectivo:  sum('efectivo'),
    tarjetas:  sum('tarjetas'),
    otros:     sum('otros'),
    sueldos:   sum('sueldos'),
    gastos:    sum('gastos'),
    final:     sum('final'),
  };
}
